import { useState, useEffect } from 'react';
import { sliders } from '../mocks/sliders';
import { collections, remainingCollections } from '../mocks/collections';
import { carpetProducts } from '../mocks/carpet-collections';
import { carpetCombinations } from '../mocks/customers';
import { catalogPages } from '../mocks/catalog';

// ---- Types ----
export interface AdminSlider {
  id: number;
  type: 'image' | 'video';
  src: string;
  poster?: string;
  title: string;
  description: string;
  duration: number;
  active: boolean;
}

export interface AdminCollection {
  id: number;
  name: string;
  category: string;
  image: string;
  images: string[];
  description?: string;
}

export interface AdminProduct {
  id: string;
  name: string;
  description: string;
  images: string[];
  sizes: string[];
  features: { label: string; value: string }[];
  category: string;
  subcategory: string;
  likes: number;
  active: boolean;
}

export interface AdminPage {
  id: string;
  name: string;
  path: string;
  label: string;
  active: boolean;
}

export interface AdminMenuItem {
  id: string;
  name: string;
  path: string;
  label: string;
  active: boolean;
  parentId?: string;
}

export interface AdminContactInfo {
  whatsapp: string;
  email: string;
  phone: string;
}

export interface AdminFeaturedBanner {
  id: string;
  title: string;
  year: string;
  image: string;
  active: boolean;
}

export interface AdminCatalogPage {
  id: number;
  image: string;
  title: string;
  active: boolean;
}

// ---- Storage Keys ----
const SLIDERS_KEY = 'admin_sliders_v2';
const COLLECTIONS_KEY = 'admin_collections_v1';
const PRODUCTS_KEY = 'admin_products_v1';
const PAGES_KEY = 'admin_pages_v1';
const MENU_KEY = 'admin_menu_v2';
const CONTACT_KEY = 'admin_contact_v1';
const CATALOG_KEY = 'admin_catalog_v1';

// Featured Banners Management
const FEATURED_BANNERS_KEY = 'admin_featured_banners';

const defaultFeaturedBanners: AdminFeaturedBanner[] = carpetCombinations.map((combo) => ({
  id: combo.id.toString(),
  title: combo.title,
  year: '2024',
  image: combo.image,
  active: true
}));

export const loadFeaturedBanners = (): AdminFeaturedBanner[] => {
  try {
    const stored = localStorage.getItem(FEATURED_BANNERS_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Failed to load featured banners:', error);
  }
  return defaultFeaturedBanners;
};

export const saveFeaturedBanners = (banners: AdminFeaturedBanner[]): void => {
  try {
    localStorage.setItem(FEATURED_BANNERS_KEY, JSON.stringify(banners));
    window.dispatchEvent(new CustomEvent('admin_banners_updated'));
  } catch (error) {
    console.error('Failed to save featured banners:', error);
  }
};

export const useAdminFeaturedBanners = () => {
  const [banners, setBanners] = useState<AdminFeaturedBanner[]>([]);

  useEffect(() => {
    setBanners(loadFeaturedBanners());

    const handleUpdate = () => {
      setBanners(loadFeaturedBanners());
    };

    window.addEventListener('admin_banners_updated', handleUpdate);
    return () => window.removeEventListener('admin_banners_updated', handleUpdate);
  }, []);

  return banners;
};

// Catalog Pages Management
const defaultCatalogPages: AdminCatalogPage[] = catalogPages.map((page) => ({
  ...page,
  active: true
}));

export const loadCatalogPages = (): AdminCatalogPage[] => {
  try {
    const stored = localStorage.getItem(CATALOG_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Failed to load catalog pages:', error);
  }
  return defaultCatalogPages;
};

export const saveCatalogPages = (pages: AdminCatalogPage[]): void => {
  try {
    localStorage.setItem(CATALOG_KEY, JSON.stringify(pages));
    window.dispatchEvent(new CustomEvent('admin_catalog_updated'));
  } catch (error) {
    console.error('Failed to save catalog pages:', error);
  }
};

export const useAdminCatalogPages = () => {
  const [pages, setPages] = useState<AdminCatalogPage[]>([]);

  useEffect(() => {
    setPages(loadCatalogPages());

    const handleUpdate = () => {
      setPages(loadCatalogPages());
    };

    window.addEventListener('admin_catalog_updated', handleUpdate);
    return () => window.removeEventListener('admin_catalog_updated', handleUpdate);
  }, []);

  return pages;
};

// ---- Default Data ----
function buildDefaultSliders(): AdminSlider[] {
  return sliders.map((slider, index) => ({
    id: index + 1,
    type: slider.type as 'image' | 'video',
    src: slider.type === 'image' ? (slider as any).image : (slider as any).video,
    poster: slider.type === 'video' ? (slider as any).thumbnail : undefined,
    title: slider.title,
    description: slider.description,
    duration: slider.duration,
    active: true,
  }));
}

function buildDefaultCollections(): AdminCollection[] {
  const all = [
    ...collections.map((c) => ({
      id: c.id,
      name: c.name,
      category: c.category,
      image: c.image,
      images: [c.image],
      description: c.description,
    })),
    ...remainingCollections.map((c) => ({
      id: c.id,
      name: c.name,
      category: c.category,
      image: c.image,
      images: [c.image],
    })),
  ];
  return all;
}

function buildDefaultProducts(): AdminProduct[] {
  return carpetProducts.map((p) => ({
    ...p,
    active: true,
  }));
}

function buildDefaultPages(): AdminPage[] {
  return [
    { id: 'home', name: 'Anasayfa', path: '/', label: 'Ana Sayfa', active: true },
    { id: 'collections', name: 'Koleksiyonlar', path: '/koleksiyonlar', label: 'Koleksiyonlar', active: true },
    { id: 'documents', name: 'Belgelerimiz', path: '/belgelerimiz', label: 'Belgelerimiz', active: true },
    { id: 'sales', name: 'Satış', path: '/satis', label: 'Satış Noktaları', active: true },
    { id: 'contact', name: 'İletişim', path: '/iletisim', label: 'İletişim', active: true },
    { id: 'hr', name: 'İnsan Kaynakları', path: '/insan-kaynaklari', label: 'İnsan Kaynakları', active: true },
    { id: 'blog', name: 'Blog', path: '/blog', label: 'Blog & Haberler', active: true },
    { id: 'history', name: 'Tarihçe', path: '/kurumsal/tarihce', label: 'Tarihçe', active: true },
    { id: 'vision', name: 'Vizyon & Misyon', path: '/kurumsal/vision-misyon', label: 'Vizyon & Misyon', active: true },
    { id: 'regions', name: 'Bölgelerimiz', path: '/kurumsal/bolgelerimiz', label: 'Bölgelerimiz', active: true },
    { id: 'certificates', name: 'Sertifikalarımız', path: '/kurumsal/sertifikalarimiz', label: 'Sertifikalarımız', active: true },
    { id: 'ecatalog', name: 'E-Katalog', path: '/kurumsal/e-katalog', label: 'E-Katalog', active: true },
    { id: 'production', name: 'Üretim Süreci', path: '/kurumsal/uretim-sureci', label: 'Üretim Süreci', active: true },
    { id: 'dealer-login', name: 'Bayi Girişi', path: '/bayi-giris', label: 'Bayi Girişi', active: true },
    { id: 'dealer-apply', name: 'Bayilik Başvuru', path: '/bayilik-basvuru', label: 'Bayilik Başvuru', active: true },
  ];
}

function buildDefaultMenuItems(): AdminMenuItem[] {
  return [
    // Ana menü öğeleri
    { id: 'menu-home', name: 'Anasayfa', path: '/', label: 'Anasayfa', active: true },
    { id: 'menu-corporate', name: 'Kurumsal', path: '#', label: 'Kurumsal', active: true },
    { id: 'menu-collections', name: 'Koleksiyonlar', path: '/koleksiyonlar', label: 'Koleksiyonlar', active: true },
    { id: 'menu-documents', name: 'Belgelerimiz', path: '/belgelerimiz', label: 'Belgelerimiz', active: true },
    { id: 'menu-sales', name: 'Satış', path: '/satis', label: 'Satış', active: true },
    { id: 'menu-contact', name: 'İletişim', path: '/iletisim', label: 'İletişim', active: true },
    { id: 'menu-hr', name: 'İnsan Kaynakları', path: '/insan-kaynaklari', label: 'İnsan Kaynakları', active: true },
    
    // Kurumsal alt menü öğeleri
    { id: 'submenu-gaziantep', name: 'Gaziantep Carpet', path: '/kurumsal/gaziantep-carpet', label: 'Gaziantep Carpet', active: true, parentId: 'menu-corporate' },
    { id: 'submenu-history', name: 'Tarihçe', path: '/kurumsal/tarihce', label: 'Tarihçe', active: true, parentId: 'menu-corporate' },
    { id: 'submenu-vision', name: 'Vizyon & Misyon', path: '/kurumsal/vizyon-misyon', label: 'Vizyon & Misyon', active: true, parentId: 'menu-corporate' },
    { id: 'submenu-production', name: 'Üretim Süreci', path: '/kurumsal/uretim-sureci', label: 'Üretim Süreci', active: true, parentId: 'menu-corporate' },
    { id: 'submenu-toptan', name: 'Toptan Halı Üretimi', path: '/kurumsal/toptan-hali-uretimi', label: 'Toptan Halı Üretimi', active: true, parentId: 'menu-corporate' },
    { id: 'submenu-regions', name: 'Bölgelerimiz', path: '/kurumsal/bolgelerimiz', label: 'Bölgelerimiz', active: true, parentId: 'menu-corporate' },
    { id: 'submenu-certificates', name: 'Sertifikalarımız', path: '/kurumsal/sertifikalarimiz', label: 'Sertifikalarımız', active: true, parentId: 'menu-corporate' },
    { id: 'submenu-ecatalog', name: 'E-Katalog', path: '/kurumsal/e-katalog', label: 'E-Katalog', active: true, parentId: 'menu-corporate' },
    { id: 'submenu-blog', name: 'Blog & Haberler', path: '/blog', label: 'Blog & Haberler', active: true, parentId: 'menu-corporate' },
  ];
}

function buildDefaultContactInfo(): AdminContactInfo {
  return {
    whatsapp: '+905001234567',
    email: 'info@monshali.com',
    phone: '+905001234567'
  };
}

// ---- Persistence Helpers ----
export function loadSliders(): AdminSlider[] {
  try {
    const raw = localStorage.getItem(SLIDERS_KEY);
    if (raw) return JSON.parse(raw) as AdminSlider[];
  } catch (_e) {
    // localStorage okunamadı, varsayılan veriler kullanılacak
  }
  return buildDefaultSliders();
}

export function saveSliders(data: AdminSlider[]): void {
  try {
    localStorage.setItem(SLIDERS_KEY, JSON.stringify(data));
    window.dispatchEvent(new CustomEvent('admin_sliders_updated'));
  } catch (_e) {
    // localStorage yazılamadı
  }
}

export function loadCollections(): AdminCollection[] {
  try {
    const raw = localStorage.getItem(COLLECTIONS_KEY);
    if (raw) return JSON.parse(raw) as AdminCollection[];
  } catch (_e) {
    // localStorage okunamadı, varsayılan veriler kullanılacak
  }
  return buildDefaultCollections();
}

export function saveCollections(data: AdminCollection[]): void {
  try {
    localStorage.setItem(COLLECTIONS_KEY, JSON.stringify(data));
    window.dispatchEvent(new CustomEvent('admin_collections_updated'));
  } catch (_e) {
    // localStorage yazılamadı
  }
}

export function loadProducts(): AdminProduct[] {
  try {
    const raw = localStorage.getItem(PRODUCTS_KEY);
    if (raw) return JSON.parse(raw) as AdminProduct[];
  } catch (_e) {
    // localStorage okunamadı, varsayılan veriler kullanılacak
  }
  return buildDefaultProducts();
}

export function saveProducts(data: AdminProduct[]): void {
  try {
    localStorage.setItem(PRODUCTS_KEY, JSON.stringify(data));
    window.dispatchEvent(new CustomEvent('admin_products_updated'));
  } catch (_e) {
    // localStorage yazılamadı
  }
}

export function loadPages(): AdminPage[] {
  try {
    const raw = localStorage.getItem(PAGES_KEY);
    if (raw) return JSON.parse(raw) as AdminPage[];
  } catch (_e) {
    // localStorage okunamadı, varsayılan veriler kullanılacak
  }
  return buildDefaultPages();
}

export function savePages(data: AdminPage[]): void {
  try {
    localStorage.setItem(PAGES_KEY, JSON.stringify(data));
    window.dispatchEvent(new CustomEvent('admin_pages_updated'));
  } catch (_e) {
    // localStorage yazılamadı
  }
}

export function loadMenuItems(): AdminMenuItem[] {
  try {
    const raw = localStorage.getItem(MENU_KEY);
    if (raw) return JSON.parse(raw) as AdminMenuItem[];
  } catch (_e) {
    // localStorage okunamadı, varsayılan veriler kullanılacak
  }
  return buildDefaultMenuItems();
}

export function saveMenuItems(data: AdminMenuItem[]): void {
  try {
    localStorage.setItem(MENU_KEY, JSON.stringify(data));
    window.dispatchEvent(new CustomEvent('admin_menu_updated'));
  } catch (_e) {
    // localStorage yazılamadı
  }
}

export function loadContactInfo(): AdminContactInfo {
  try {
    const raw = localStorage.getItem(CONTACT_KEY);
    if (raw) return JSON.parse(raw) as AdminContactInfo;
  } catch (_e) {
    // localStorage okunamadı, varsayılan veriler kullanılacak
  }
  return buildDefaultContactInfo();
}

export function saveContactInfo(data: AdminContactInfo): void {
  try {
    localStorage.setItem(CONTACT_KEY, JSON.stringify(data));
    window.dispatchEvent(new CustomEvent('admin_contact_updated'));
  } catch (_e) {
    // localStorage yazılamadı
  }
}

// ---- React Hooks ----
export function useAdminSliders() {
  const [data, setData] = useState<AdminSlider[]>(loadSliders);

  useEffect(() => {
    const handler = () => setData(loadSliders());
    window.addEventListener('admin_sliders_updated', handler);
    return () => window.removeEventListener('admin_sliders_updated', handler);
  }, []);

  return data;
}

export function useAdminCollections() {
  const [data, setData] = useState<AdminCollection[]>(loadCollections);

  useEffect(() => {
    const handler = () => setData(loadCollections());
    window.addEventListener('admin_collections_updated', handler);
    return () => window.removeEventListener('admin_collections_updated', handler);
  }, []);

  return data;
}

export function useAdminProducts() {
  const [data, setData] = useState<AdminProduct[]>(loadProducts);

  useEffect(() => {
    const handler = () => setData(loadProducts());
    window.addEventListener('admin_products_updated', handler);
    return () => window.removeEventListener('admin_products_updated', handler);
  }, []);

  return data;
}

export function useAdminPages() {
  const [data, setData] = useState<AdminPage[]>(loadPages);

  useEffect(() => {
    const handler = () => setData(loadPages());
    window.addEventListener('admin_pages_updated', handler);
    return () => window.removeEventListener('admin_pages_updated', handler);
  }, []);

  return data;
}

export function useAdminMenuItems() {
  const [data, setData] = useState<AdminMenuItem[]>(loadMenuItems);

  useEffect(() => {
    const handler = () => setData(loadMenuItems());
    window.addEventListener('admin_menu_updated', handler);
    return () => window.removeEventListener('admin_menu_updated', handler);
  }, []);

  return data;
}

export function useAdminContactInfo() {
  const [data, setData] = useState<AdminContactInfo>(loadContactInfo);

  useEffect(() => {
    const handler = () => setData(loadContactInfo());
    window.addEventListener('admin_contact_updated', handler);
    return () => window.removeEventListener('admin_contact_updated', handler);
  }, []);

  return data;
}

// ---- Authentication Hook ----
const AUTH_KEY = 'admin_auth_token';

export function useAdminStore() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    try {
      return localStorage.getItem(AUTH_KEY) === 'authenticated';
    } catch {
      return false;
    }
  });

  const login = (email: string, password: string): boolean => {
    if (email === 'admin@admin.com' && password === 'admin123') {
      try {
        localStorage.setItem(AUTH_KEY, 'authenticated');
        setIsAuthenticated(true);
        return true;
      } catch {
        return false;
      }
    }
    return false;
  };

  const logout = () => {
    try {
      localStorage.removeItem(AUTH_KEY);
      setIsAuthenticated(false);
    } catch {
      // localStorage temizlenemedi
    }
  };

  return { isAuthenticated, login, logout };
}